import { useCallback, useMemo, useRef, useState } from "react";
import type { TimeseriesPoint } from "@/types";

const STACKED_BASE = [
  { key: "successful", label: "Successful" },
  { key: "failed_second_factor", label: "Failed Second Factor" },
  { key: "invalid_password", label: "Invalid Password" },
  { key: "user_not_found", label: "User Not Found" },
] as const;

const BLOCKED_SERIES = { key: "blocked", label: "Blocked" } as const;
const WOULD_BLOCK_SERIES = { key: "would_block", label: "Would block" } as const;
export const LINE_SERIES = { key: "queries", label: "Queries" } as const;

export type SeriesKey =
  | (typeof STACKED_BASE)[number]["key"]
  | "blocked"
  | "would_block"
  | "queries";

export interface SeriesDef {
  key: SeriesKey;
  label: string;
}

export function stackedSeries(auditMode: boolean): readonly SeriesDef[] {
  return [...STACKED_BASE, auditMode ? WOULD_BLOCK_SERIES : BLOCKED_SERIES];
}

export const PRESETS = [
  { label: "1h", minutes: 60 },
  { label: "4h", minutes: 240 },
  { label: "24h", minutes: 1440 },
  { label: "All", minutes: Infinity },
] as const;

function ensureUtc(iso: string): string {
  if (!/[Z+-]\d{0,4}$/.test(iso)) return iso + "Z";
  return iso;
}

const SERIES_KEYS: readonly (keyof Omit<TimeseriesPoint, "timestamp">)[] = [
  "successful",
  "blocked",
  "user_not_found",
  "invalid_password",
  "failed_second_factor",
  "queries",
  "would_block",
];

const ZERO_POINT: Omit<TimeseriesPoint, "timestamp"> = {
  successful: 0,
  blocked: 0,
  user_not_found: 0,
  invalid_password: 0,
  failed_second_factor: 0,
  queries: 0,
  would_block: 0,
};

/** Max synthetic points to insert between any two consecutive data points. */
const MAX_INTERP_STEPS = 60;

/** Append a zero-value point at current time if the data doesn't reach "now". */
function extendToNow(raw: TimeseriesPoint[]): TimeseriesPoint[] {
  if (!raw.length) return raw;
  const now = Date.now();
  const lastTs = new Date(ensureUtc(raw[raw.length - 1].timestamp)).getTime();
  if (now - lastTs <= 60_000) return raw;
  return [...raw, { ...ZERO_POINT, timestamp: new Date(now).toISOString() }];
}

function lerp(from: number | undefined, to: number | undefined, t: number): number {
  const a = from ?? 0;
  return Math.round(a + ((to ?? 0) - a) * t);
}

/**
 * Extend dataset to current time and fill time gaps with linearly
 * interpolated points so the area chart renders smooth transitions.
 */
function interpolateGaps(raw: TimeseriesPoint[]): TimeseriesPoint[] {
  const data = extendToNow(raw);
  if (data.length < 2) return data;

  const times = data.map((d) => new Date(ensureUtc(d.timestamp)).getTime());

  // Compute median interval to detect "normal" spacing
  const intervals: number[] = [];
  for (let i = 1; i < times.length; i++) {
    intervals.push(times[i] - times[i - 1]);
  }
  intervals.sort((a, b) => a - b);
  const median = intervals[Math.floor(intervals.length / 2)];
  if (median <= 0) return data;

  const result: TimeseriesPoint[] = [];

  for (let i = 0; i < data.length; i++) {
    result.push(data[i]);

    if (i >= data.length - 1) continue;
    const gap = times[i + 1] - times[i];
    if (gap <= median * 1.5) continue;

    const steps = Math.min(Math.round(gap / median), MAX_INTERP_STEPS);
    const from = data[i];
    const to = data[i + 1];

    for (let s = 1; s < steps; s++) {
      const t = s / steps;
      const pt: TimeseriesPoint = {
        ...ZERO_POINT,
        timestamp: new Date(times[i] + gap * t).toISOString(),
      };
      for (const k of SERIES_KEYS) {
        pt[k] = lerp(from[k], to[k], t);
      }
      result.push(pt);
    }
  }

  return result;
}

export interface DayBoundary {
  timestamp: string;
  label: string;
}

const TARGET_POINTS = 250;

/** Human-friendly step sizes in ms, ascending. */
const NICE_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 240, 360, 480, 720, 1440].map(
  (m) => m * 60_000,
);

function formatStepLabel(ms: number): string {
  const mins = ms / 60_000;
  if (mins < 60) return `${mins}m`;
  const hrs = mins / 60;
  if (Number.isInteger(hrs)) return `${hrs}h`;
  return `${mins}m`;
}

/** Find local-midnight day boundaries within chartData, snapped to the nearest bucket. */
function computeDayBoundaries(chartData: TimeseriesPoint[]): DayBoundary[] {
  if (chartData.length < 2) return [];
  const startMs = new Date(ensureUtc(chartData[0].timestamp)).getTime();
  const endMs = new Date(ensureUtc(chartData[chartData.length - 1].timestamp)).getTime();
  if (endMs - startMs < 24 * 3_600_000) return [];

  const midnights: DayBoundary[] = [];
  const first = new Date(startMs);
  const cursor = new Date(first.getFullYear(), first.getMonth(), first.getDate() + 1);
  while (cursor.getTime() <= endMs) {
    const target = cursor.getTime();
    let closest = chartData[0].timestamp;
    let closestDiff = Math.abs(new Date(ensureUtc(chartData[0].timestamp)).getTime() - target);
    for (const pt of chartData) {
      const diff = Math.abs(new Date(ensureUtc(pt.timestamp)).getTime() - target);
      if (diff < closestDiff) {
        closest = pt.timestamp;
        closestDiff = diff;
      }
    }
    midnights.push({
      timestamp: closest,
      label: cursor.toLocaleDateString([], { month: "short", day: "numeric" }),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return midnights;
}

/** Pick the nice step size whose point count is closest to TARGET_POINTS. */
function pickStep(duration: number): number {
  let best = NICE_STEPS[0];
  let bestDiff = Math.abs(duration / best - TARGET_POINTS);
  for (let i = 1; i < NICE_STEPS.length; i++) {
    const diff = Math.abs(duration / NICE_STEPS[i] - TARGET_POINTS);
    if (diff < bestDiff) {
      best = NICE_STEPS[i];
      bestDiff = diff;
    }
  }
  return best;
}

/**
 * Generate a time grid spanning [start, end] with ~TARGET_POINTS buckets.
 * The step adapts dynamically: 1m for short windows, 15m+ for longer ones.
 * Real data points are aggregated (summed) into their nearest bucket.
 */
function fillWindow(start: number, end: number, points: TimeseriesPoint[]): TimeseriesPoint[] {
  const duration = end - start;
  const step = pickStep(duration);
  const count = Math.floor(duration / step) + 1;

  // Aggregate real data into buckets
  const buckets = new Map<number, TimeseriesPoint>();
  for (const pt of points) {
    const ts = new Date(ensureUtc(pt.timestamp)).getTime();
    const slot = Math.round((ts - start) / step) * step + start;
    const existing = buckets.get(slot);
    if (existing) {
      for (const k of SERIES_KEYS) existing[k] = (existing[k] ?? 0) + (pt[k] ?? 0);
    } else {
      buckets.set(slot, { ...pt, timestamp: new Date(slot).toISOString() });
    }
  }

  const result: TimeseriesPoint[] = [];
  for (let i = 0; i < count; i++) {
    const ts = start + i * step;
    const real = buckets.get(ts);
    result.push(real ?? { ...ZERO_POINT, timestamp: new Date(ts).toISOString() });
  }
  return result;
}

export function useTimeSeriesState(rawData: TimeseriesPoint[], auditMode: boolean) {
  const data = useMemo(() => interpolateGaps(rawData), [rawData]);
  const series = useMemo(() => stackedSeries(auditMode), [auditMode]);

  const [activePreset, setActivePreset] = useState("All");
  const [lastPreset, setLastPreset] = useState("All");
  const [hidden, setHidden] = useState<Set<SeriesKey>>(new Set<SeriesKey>(["queries"]));
  const [window, setWindow] = useState<{ start: number; end: number } | null>(null);

  const applyPreset = useCallback(
    (label: string) => {
      const preset = PRESETS.find((p) => p.label === label);
      if (!preset) return;
      if (preset.minutes !== Infinity) {
        const now = Date.now();
        setWindow({ start: now - preset.minutes * 60_000, end: now });
      } else {
        setWindow(null);
      }
      setActivePreset(label);
      setLastPreset(label);
    },
    [],
  );

  const toggleSeries = useCallback((key: SeriesKey) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Filter data to the active window, then build a ~250-point grid
  const { chartData, granularity } = useMemo(() => {
    let start: number, end: number, filtered: TimeseriesPoint[];
    if (window) {
      start = window.start;
      end = window.end;
      filtered = data.filter((d) => {
        const ts = new Date(ensureUtc(d.timestamp)).getTime();
        return ts >= start && ts <= end;
      });
    } else if (data.length >= 2) {
      start = new Date(ensureUtc(data[0].timestamp)).getTime();
      end = new Date(ensureUtc(data[data.length - 1].timestamp)).getTime();
      filtered = data;
    } else {
      return { chartData: data, granularity: "" };
    }
    const stepMs = pickStep(end - start);
    return { chartData: fillWindow(start, end, filtered), granularity: formatStepLabel(stepMs) };
  }, [data, window]);

  // Label for the "All" preset — fixed data window
  const allLabel = "72h";

  // Compute local-midnight day boundaries visible in the current chart range
  const dayBoundaries = useMemo(() => computeDayBoundaries(chartData), [chartData]);

  // Label for the first visible point's local day, used as a chart overlay so
  // the day is obvious even when the zoom window is entirely within one day.
  const visibleStartLabel = useMemo(() => {
    if (!chartData.length) return "";
    const d = new Date(ensureUtc(chartData[0].timestamp));
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }, [chartData]);

  const yMax = useMemo(() => computeYMax(chartData, hidden, series), [chartData, hidden, series]);

  const drag = useDragZoom({ setActivePreset, setWindow });

  return { sliced: chartData, chartData, yMax, hidden, series, activePreset, lastPreset, granularity, allLabel, dayBoundaries, visibleStartLabel, ...drag, applyPreset, toggleSeries };
}

function computeYMax(chartData: TimeseriesPoint[], hidden: Set<SeriesKey>, series: readonly SeriesDef[]) {
  let max = 0;
  for (const pt of chartData) {
    let sum = 0;
    for (const s of series) {
      if (!hidden.has(s.key)) sum += pt[s.key] ?? 0;
    }
    const line = hidden.has(LINE_SERIES.key) ? 0 : (pt[LINE_SERIES.key] ?? 0);
    if (sum > max) max = sum;
    if (line > max) max = line;
  }
  return Math.ceil(max * 1.1) || 10;
}

interface DragZoomOpts {
  setActivePreset: (v: string) => void;
  setWindow: (v: { start: number; end: number } | null) => void;
}

function useDragZoom({ setActivePreset, setWindow }: DragZoomOpts) {
  const [selStart, setSelStart] = useState<string | null>(null);
  const [selEnd, setSelEnd] = useState<string | null>(null);
  const dragLabel = useRef<string | null>(null);

  const onMouseDown = useCallback(
    (e: { activeTooltipIndex?: number; activeLabel?: string }) => {
      if (e.activeLabel) {
        dragLabel.current = e.activeLabel;
        setSelStart(e.activeLabel);
        setSelEnd(null);
      }
    },
    [],
  );

  const onMouseMove = useCallback(
    (e: { activeLabel?: string }) => {
      if (dragLabel.current != null && e.activeLabel) setSelEnd(e.activeLabel);
    },
    [],
  );

  const onMouseUp = useCallback(
    (e: { activeLabel?: string }) => {
      const fromLabel = dragLabel.current;
      const toLabel = e.activeLabel;
      dragLabel.current = null;

      if (fromLabel && toLabel && fromLabel !== toLabel) {
        const fromMs = new Date(ensureUtc(fromLabel)).getTime();
        const toMs = new Date(ensureUtc(toLabel)).getTime();
        const leftMs = Math.min(fromMs, toMs);
        const rightMs = Math.max(fromMs, toMs);

        if (rightMs - leftMs > 60_000) {
          setWindow({ start: leftMs, end: rightMs });
          setActivePreset("");
        }
      }
      setSelStart(null);
      setSelEnd(null);
    },
    [setActivePreset, setWindow],
  );

  return { selStart, selEnd, onMouseDown, onMouseMove, onMouseUp };
}
